import { PortableText, PortableTextProps } from '@portabletext/react'
import { sectionBorders } from '../or-flexible-block'
import { externalLinkProps } from './external-link-props'
import { ComponentProps, PropsWithChildren, createContext, useContext, useMemo } from 'react'

export type OrTextBlockContentProps = ComponentProps<typeof OrTextBlock>['content']
export type OrTextBlockProps = {
  content: PortableTextProps['value']
  className?: string
}

/** Components lack `h1` and `blockquote` elements since the associated content type have these disabled. See `smTextBlock` content type for details */
export const textBlockBaseComponents: PortableTextProps['components'] = {
  block: {
    h2: ({ children }) => <h2 className="font-heading font-medium leading-1.2 text-32 lg:text-48 mb-8">{children}</h2>,
    h3: ({ children }) => (
      <h3 className="font-heading font-medium leading-1.3 lg:leading-1.2 text-24 lg:text-40 mb-6">{children}</h3>
    ),
    h4: ({ children }) => (
      <h4 className="font-heading font-medium leading-1.3 lg:leading-1.2 text-20 lg:text-32 mb-5">{children}</h4>
    ),
    h5: ({ children }) => <h5 className="font-heading font-medium leading-1.3 text-16 lg:text-24 mb-4">{children}</h5>,
    h6: ({ children }) => <h6 className="font-heading font-medium leading-1.3 text-14 lg:text-20 mb-4">{children}</h6>,
    normal: ({ children }) => <p className="leading-1.5 mb-8">{children}</p>,
  },
  marks: {
    // Assumes this is a simple inlined text, otherwise you'd need to look into the children prop and create the used tree.
    //  Uses inline primary variant of at-link-button in Figma
    link: ({ text, value: { href } }) => (
      <a
        href={href}
        {...externalLinkProps(href)}
        className="underline break-words leading-1.5 text-primary-500 hover:text-primary-700 focus-visible:text-primary-700 active:text-primary-900 focus-visible:outline-offset-4 focus-visible:outline focus-visible:outline-1 focus-visible:outline-neutrals-900 focus:outline-none [&:not([href])]:text-neutrals-500"
      >
        {text}
      </a>
    ),
  },
  list: ({ children, value: { level, listItem } }) => {
    const bulletClasses = (level: number) => {
      if (level % 3 === 0) return 'list-[square] px-8'
      if (level % 2 === 0) return 'list-[circle] px-4'
      return 'list-disc'
    }
    const numberClasses = (level: number) => {
      if (level % 3 === 0) return 'list-[lower-roman] px-8'
      if (level % 2 === 0) return 'list-[lower-alpha] px-4'
      return 'list-decimal'
    }

    if (listItem === 'bullet') return <ul className={`${bulletClasses(level)} list-inside`}>{children}</ul>

    return <ol className={`${numberClasses(level)} list-inside`}>{children}</ol>
  },
  listItem: ({ children }) => <li className="leading-1.5 last:mb-3">{children}</li>,
}

export const OrTextBlock = ({ content, className = '' }: OrTextBlockProps) => {
  const { render } = useTextBlockImageRenderer()
  const components = useMemo(
    () => ({
      ...textBlockBaseComponents,
      types: {
        imageReference: ({ value }: { value: { image: unknown } }) => {
          const { src, alt, width, height } = render(value.image)
          return <img className="mx-auto" src={src} alt={alt} width={width} height={height} />
        },
      },
    }),
    [render]
  )
  return (
    <section className={`${sectionBorders} ${className} !pt-0`}>
      <PortableText value={content} components={components} />
    </section>
  )
}

interface TextBlockImageRenderer {
  render(asset: unknown): { src: string; alt?: string; width?: number; height?: number }
}

const TextBlockImageRendererContext = createContext(undefined as TextBlockImageRenderer | undefined)

export const TextBlockImageRendererProvider = ({ children, render }: PropsWithChildren<TextBlockImageRenderer>) => (
  <TextBlockImageRendererContext.Provider value={{ render }}>{children}</TextBlockImageRendererContext.Provider>
)

const useTextBlockImageRenderer = () => {
  const value = useContext(TextBlockImageRendererContext)
  if (!value) {
    throw new Error('This component must be a child of TextBlockImageRendererProvider')
  }

  return value
}
